/* eslint-disable no-console */
import { useField, useFormikContext } from 'formik';
import FormikAutocomplete from './FormikAutocomplete';
import { FormikDatePicker } from './FormikDatePicker';
import FormikSelect from './FormikSelect';
import FormikTextField from './FormikTextField';
import { FormikRadioButton } from './FormikRadioButton';
import { FormikUploadFile } from './FormikUploadFile';
import FormikMultiSelect from './FormikMultiSelect';
import FormikColorPicker from './FormikColorPicker';
import FormikTagInput from './FormikTagInput';
import FormikCheckBox from './FormikCheckBox';
import { getFormattedPhoneNumber } from 'src/utils/CommonFunctions';
import FormikPastDatePicker from './FormikPastDatePicker';
import FormikFutureDatePicker from './FormikFutureDatePicker';
import { Skeleton, Box } from '@mui/material';

/** Wave skeleton matches outlined input slot height (~56px). */
export default function FormikFields({
  fieldName,
  fieldLabel,
  fieldType,
  options,
  disabled,
  isErrorField,
  dependency,
  initialValue,
  multiSelect,
  placeHolder,
  multipleTagInput,
  placement,
  listUI,
  lengthLimit,
  existingFiles,
  helpIcon, // Add this
  helpContent,
  descriptions,
  loading,
}) {
  const [field] = useField(fieldName);
  const { setFieldValue } = useFormikContext();
  const regex = fieldName === 'EIN' ? /^[-0-9]*$/ : /^[0-9]*$/;

  if (
    loading &&
    (
      fieldType === 'text' ||
      fieldType === 'note' ||
      fieldType === 'email' ||
      fieldType === 'password' ||
      fieldType === 'number' ||
      fieldType === 'mobile' ||
      fieldType === 'select'
    )
  ) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ typography: 'body2', mb: 0.5, color: 'text.primary' }}>
          {fieldLabel}
        </Box>
        <Skeleton
          variant="rounded"
          animation="wave"
          height={56}
          sx={{ borderRadius: 1 }}
        />
      </Box>
    );
  }

  const handleChange = (e, selectValue) => {
    if (fieldType === 'colorPicker' || fieldType === 'tagInput') {
      setFieldValue(fieldName, e);
    } else if (fieldType === 'file') {
      setFieldValue(fieldName, e);
    } else if (fieldType === 'autocomplete') {
      setFieldValue(fieldName, selectValue);
    } else if (fieldType === 'check') {
      setFieldValue(fieldName, e.target.checked);
    } else if (fieldType === 'number') {
      if (
        lengthLimit &&
        e.target.value?.length > lengthLimit &&
        regex.test(e.target.value)
      ) {
        const validValue = e.target.value?.slice(0, lengthLimit);
        setFieldValue(fieldName, validValue);
      } else if (regex.test(e.target.value)) {
        setFieldValue(fieldName, e.target.value);
      }
    } else if (fieldType === 'mobile') {
      let val = getFormattedPhoneNumber(e.target.value);
      setFieldValue(fieldName, val);
    } else if (e.target.value[0] !== ' ') {
      setFieldValue(fieldName, e.target.value);
    }

    if (dependency) {
      if (fieldType === 'colorPicker') {
        dependency(e, field);
      } else if (fieldType === 'file') {
        dependency(e, field);
      } else if (
        selectValue &&
        selectValue !== 'autocomplete-text' &&
        fieldType !== 'select'
      ) {
        dependency(selectValue, field);
      } else if (dependency) {
        if (fieldType === 'mobile') {
          let val = getFormattedPhoneNumber(e.target.value);
          dependency(val, field);
        } else if (fieldType === 'number') {
          if (
            lengthLimit &&
            e.target.value?.length > lengthLimit &&
            regex.test(e.target.value)
          ) {
            const validValue = e.target.value?.slice(0, lengthLimit);
            dependency(validValue, field);
          } else if (regex.test(e.target.value)) {
            dependency(e.target.value, field);
          }
        } else {
          dependency(e.target.value, field);
        }
      }
    }
  };

  const validateOpeningDate = (openingDate) => {
    if (dependency) {
      dependency(openingDate);
    }
    setFieldValue(fieldName, openingDate);
  };

  return (
    // For Select Autocomplete Field
    fieldType === 'autocomplete' ? (
      <FormikAutocomplete
        initialValue={initialValue}
        disabled={disabled}
        placeHolder={placeHolder}
        name={fieldName}
        label={fieldLabel}
        type={fieldType}
        fieldValue={field}
        handleChange={handleChange}
        options={options}
      />
    ) : // For Select Text Field
      fieldType === 'text' ||
        fieldType === 'note' ||
        fieldType === 'email' ||
        fieldType === 'number' ||
        fieldType === 'password' ||
        fieldType === 'mobile' ? (
        <FormikTextField
          disabled={disabled}
          name={fieldName}
          label={fieldLabel}
          type={fieldType === 'number' ? 'text' : fieldType}
          fieldValue={field}
          handleChange={handleChange}
          isErrorField={isErrorField}
          required={!!field.required}
          multipleTagInput={multipleTagInput}
        />
      ) : // For Select Type Field
        fieldType === 'select' ? (
          <FormikSelect
            initialValue={initialValue}
            disabled={disabled}
            label={fieldLabel}
            fieldValue={field}
            options={options}
            handleChange={handleChange}
            isErrorField={isErrorField}
            required={!!field.required}
            name={fieldName}
            type={fieldType}
            multiSelect={multiSelect}
            helpIcon={helpIcon} // Add this
            helpContent={helpContent} 
            descriptions={descriptions}
          />
        ) : fieldType === 'multiselect' ? (
          <FormikMultiSelect
            initialValue={initialValue}
            disabled={disabled}
            label={fieldLabel}
            fieldValue={field}
            options={options}
            handleChange={handleChange}
            isErrorField={isErrorField}
            name={fieldName}
            type={fieldType}
            multiSelect={multiSelect}
          />
        ) : fieldType === 'date' ||
          fieldType === 'startDate' ||
          fieldType === 'endDate' ? (
          <FormikDatePicker
            label={fieldLabel}
            onChange={validateOpeningDate}
            disabled={disabled}
            initialValue={initialValue}
            fieldValue={field}
            disablePast={fieldType === 'startDate'}
            disableFuture={fieldType === 'endDate'}
          />
        ) : fieldType === 'pastDate' ? (
          <FormikPastDatePicker
            onChange={validateOpeningDate}
            label={fieldLabel}
            disabled={disabled}
            initialValue={initialValue}
            fieldValue={field.value}
          />
        ) : fieldType === 'futureDate' ? (
          <FormikFutureDatePicker
            onChange={validateOpeningDate}
            label={fieldLabel}
            disabled={disabled}
            initialValue={initialValue}
            fieldValue={field.value}
          />
        ) : fieldType === 'radio' ? (
          <FormikRadioButton
            disabled={disabled}
            label={fieldLabel}
            fieldValue={field}
            options={options}
            handleChange={handleChange}
          />
        ) : fieldType === 'file' ? (
          <FormikUploadFile
            setFieldValue={setFieldValue}
            disabled={disabled}
            name={fieldName}
            label={fieldLabel}
            placement={placement}
            type={fieldType}
            fieldValue={field}
            listUI={listUI}
            handleChange={handleChange}
            multiSelect={multiSelect}
            isErrorField={isErrorField}
            existingFiles={existingFiles}
          />
        ) : fieldType === 'colorPicker' ? (
          <FormikColorPicker
            type={fieldType}
            name={fieldName}
            fieldValue={field}
            label={fieldLabel}
            handleChange={handleChange}
          />
        ) : fieldType === 'tagInput' ? (
          <FormikTagInput
            type={fieldType}
            name={fieldName}
            fieldValue={field}
            label={fieldLabel}
            handleChange={handleChange}
          />
        ) : fieldType === 'check' ? (
          <FormikCheckBox
            type={fieldType}
            name={fieldName}
            fieldValue={field}
            label={fieldLabel}
            handleChange={handleChange}
          />
        ) : (
          ''
        )
  );
}
